import React, { useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { sunboundPayProxy } from '@proxies';
import currency from "currency.js";
import { Enums, helpers, Constants } from "@shared";
import { useSpinnerContext } from '@contexts';
import { useFormSubmission, useAllowedPaymentTypes } from "@hooks";
import { DateTime } from "luxon";
import { StatusCodes } from "http-status-codes";

const ConfirmPayment = () => {

    const navigate = useNavigate(), location = useLocation();
    const { setSpinnerShow } = useSpinnerContext();
    const { allowedPaymentTypes } = useAllowedPaymentTypes();

    const {
        state: {
            currentBill,
            amount,
            account: {
                idPayor,
                paymentPreferences: { defaultPaymentMethod },
                accountPreferences: { paymentTypeFees }
            },
            idempotencyKey
        } } = location;
    const { dueDate, invitations } = currentBill;
    const month = helpers.monthName(dueDate);

    const { idPaymentType } = defaultPaymentMethod;
    const paymentMethodIsAllowed = allowedPaymentTypes.includes(idPaymentType);

    const invite = invitations.find(i => i.idPayor === idPayor);

    const { setFormState, clearFormState, hasFormError, formError } = useFormSubmission();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const pay = async () => {
        if (!paymentMethodIsAllowed) {
            setFormState({
              userMessage:
                `Payment type invalid. Please use another payment method.`,
              handled: true
            });
            return;
        }
        setSpinnerShow(true);
        try {
            clearFormState();
            await sunboundPayProxy.pay(amount, invite.idInvitation, idempotencyKey);
            helpers.navigateNext(location, navigate);
        } catch (error) {
            if (error.response?.status >= StatusCodes.INTERNAL_SERVER_ERROR)
                setFormState({ userMessage: Constants.Errors.PAYMENT_PLATFORM_ERROR, handled: false });
            else {
                const { response: { data: { extendedError: { handled = false, userMessage = null } = {} } = {} } = {} } = error;
                setFormState({ userMessage, handled });
            }

        } finally {
            setSpinnerShow(false);
        }
    };

    const addDefaultPM = () => {
        helpers.navigateTo(`/payment-method-selector`, location, navigate, { addDefaultPM: true });
    };

    const subTotal = currency(amount);

    const ptFeeRule = paymentTypeFees.find(f => f.idPaymentType === idPaymentType);
    let ptfee;
    if (ptFeeRule.idFeeType === Enums.FeeType.Fixed)
        ptfee = currency(ptFeeRule.fee);
    else
        ptfee = currency(ptFeeRule.fee, { precision: 3 }).multiply(amount);

    const total = subTotal.add(ptfee);

    return (
        <div className="flex flex-col bg-white p-6 w-full rounded-b-lg min-h-sb pb-12 items-center gap-y-4">
            <h1 className="text-center font-bold text-2xl">{`${month} Balance`}</h1>
            <div className="flex flex-col items-center p-5 rounded-lg border-2 shadow-md gap-y-4 bg-sb-frame w-full">
                <h1 className="text-center font-bold text-xl self-start">Confirm Payment Information</h1>
                <div className="w-full border-t-2 border-gray-300"></div>
                <p className="p-3">
                    By clicking <b>Confirm Payment</b> you are authorizing Sunbound to debit your account for the total amount specified.
                </p>
                <div className="grid grid-cols-2 w-5/6 gap-y-2">
                    <div>Payment Date</div>
                    <div className="text-right">{DateTime.now().toFormat(`DDD`)}</div>
                    <div className="font-bold">Subtotal</div>
                    <div className="font-bold text-right">{subTotal.format()}</div>
                    <div className="font-bold">Processing Fee</div>
                    <div className="font-bold text-right">{currency(ptfee).format()}</div>
                    <div className="font-bold">Total</div>
                    <div className="font-bold text-right">{total.format()}</div>

                </div>
                <button type="button" className="btn btn-primary font-bold rounded-full my-4" onClick={pay}>Confirm Payment</button>
                {
                    hasFormError &&
                    <div className="w-full">
                        <span className="text-red-400 font-semibold">{formError.userMessage}&nbsp;
                            {
                                !formError.handled &&
                                <>
                                    If you have questions, please visit our <a className="underline" href={Constants.Help.FAQ} target="_blank" rel="noreferrer">FAQ</a> or contact us at <a href={`mailto:${Constants.Help.Email}?subject=Payment support`}>{Constants.Help.Email}</a>
                                </>
                            }
                            {
                                formError.handled &&
                                <button className="text-red-400 background-transparent font-bold outline-none underline focus:outline-none ease-linear transition-all duration-150"
                                    type="button"
                                    onClick={addDefaultPM}                        >
                                    Add another payment method.
                                </button>
                            }
                        </span>
                    </div>
                }
            </div>
        </div>
    );
};

export default ConfirmPayment;
